import type { Corners, Matrix3x3 } from './types.js';

// =============================================================================
// Homography (Direct Linear Transform) — 4-point correspondence.
// Reference: poc/day1-warp/ (Day 1 PoC validated perspective-correct warp).
// =============================================================================

/**
 * Compute 3x3 homography (row-major) mapping src quad → dst quad.
 * @throws if the 4 points are (near-)collinear.
 */
export function computeHomography(src: Corners, dst: Corners): Matrix3x3 {
  const A: number[][] = [];
  const b: number[] = [];
  for (let i = 0; i < 4; i++) {
    const s = src[i] as { x: number; y: number };
    const d = dst[i] as { x: number; y: number };
    A.push([s.x, s.y, 1, 0, 0, 0, -s.x * d.x, -s.y * d.x]);
    b.push(d.x);
    A.push([0, 0, 0, s.x, s.y, 1, -s.x * d.y, -s.y * d.y]);
    b.push(d.y);
  }
  const h = solveLinear(A, b);
  return [h[0]!, h[1]!, h[2]!, h[3]!, h[4]!, h[5]!, h[6]!, h[7]!, 1] as const;
}

/**
 * Gaussian elimination with partial pivoting. Destroys input.
 * A: n×n matrix, b: length-n RHS. Returns length-n solution vector.
 */
export function solveLinear(A: number[][], b: number[]): number[] {
  const n = A.length;
  const M: number[][] = new Array(n);
  for (let i = 0; i < n; i++) {
    const row = A[i]!;
    M[i] = [...row, b[i]!];
  }
  for (let k = 0; k < n; k++) {
    let piv = k;
    for (let r = k + 1; r < n; r++) {
      if (Math.abs(M[r]![k]!) > Math.abs(M[piv]![k]!)) piv = r;
    }
    if (piv !== k) {
      const tmp = M[k]!;
      M[k] = M[piv]!;
      M[piv] = tmp;
    }
    const pivot = M[k]![k]!;
    if (Math.abs(pivot) < 1e-12) {
      throw new Error('Singular matrix: 4 points are (near-)collinear');
    }
    for (let r = 0; r < n; r++) {
      if (r === k) continue;
      const f = M[r]![k]! / pivot;
      if (f === 0) continue;
      for (let c = k; c <= n; c++) {
        M[r]![c] = M[r]![c]! - f * M[k]![c]!;
      }
    }
  }
  const x: number[] = new Array(n);
  for (let i = 0; i < n; i++) x[i] = M[i]![n]! / M[i]![i]!;
  return x;
}

/** Invert a 3x3 matrix (row-major). @throws if singular. */
export function invert3x3(m: Matrix3x3): Matrix3x3 {
  const a = m[0], b = m[1], c = m[2];
  const d = m[3], e = m[4], f = m[5];
  const g = m[6], h = m[7], i = m[8];
  const det = a * (e * i - f * h) - b * (d * i - f * g) + c * (d * h - e * g);
  if (Math.abs(det) < 1e-12) {
    throw new Error('Singular homography: determinant is zero');
  }
  const invDet = 1 / det;
  return [
    (e * i - f * h) * invDet,
    -(b * i - c * h) * invDet,
    (b * f - c * e) * invDet,
    -(d * i - f * g) * invDet,
    (a * i - c * g) * invDet,
    -(a * f - c * d) * invDet,
    (d * h - e * g) * invDet,
    -(a * h - b * g) * invDet,
    (a * e - b * d) * invDet,
  ] as const;
}

/**
 * Convert row-major [a,b,c,d,e,f,g,h,i] to column-major — GL mat3 upload order.
 * regl's `mat3` uniform expects column-major 9-element array.
 */
export function toColumnMajor(m: Matrix3x3): number[] {
  return [m[0], m[3], m[6], m[1], m[4], m[7], m[2], m[5], m[8]];
}
